//React
import { FunctionComponent } from "react";

//Models
import { Result } from "../../models/result";

//App Hooks
import {
	useAppDispatch,
	useAppSelector,
} from "../../../system/redux/hooks/redux-store-hooks";

//Actions
import {
	clearSelectedResult,
	setSelectedResult,
} from "../../redux/slices/result-list-slice";

//Components
import ResultImage from "./result-image";

//Util
import { isStringEmpty } from "drh-js-library/dist/drh-string-utils/is-string-empty";
import { getPriceMinLabel } from "../../functions/strings/get-price-min-label";
import { getMinMaxLabel } from "../../functions/strings/get-min-max-label";
import { getSizeLabel } from "../../functions/strings/get-size-label";
import { selectIsMobile } from "../../../system/redux/selectors/select-is-mobile";

interface ResultContentProps {
	result: Result;
	isInInfoWindow: boolean;
}

/** Displays the result card for the given community result */
const ResultCard: FunctionComponent<ResultContentProps> = (props) => {
	//Properties
	const { result, isInInfoWindow } = props;

	//Store Dispatch
	const dispatch = useAppDispatch();

	const isMobile = useAppSelector(selectIsMobile);

	//State Values
	const hasAvailableHomes = result?.availableHomes > 0;

	//JSX
	return (
		<div
			className="coveo-list-layout CoveoResult"
			role="listitem"
			key={result.uniqueId}
			//Only use the hover events in desktop mode
			onMouseEnter={() => !isMobile && dispatch(setSelectedResult(result))}
			onMouseLeave={() => !isMobile && dispatch(clearSelectedResult())}
		>
			<a href={result.url} className="CoveoResultLink">
				<div className="home-info community">
					<ResultImage result={result} />

					<div className="info-frame">
						<div className="home-info__community-status">
							<span
								className="home-info__community-status-color"
								style={{ background: result.activationStateHex }}
							></span>
							<span>{result.activationState}</span>
						</div>
						<div className="title CoveoResultTitle">
							<h2>{result.title}</h2>
						</div>
						<div className="home-info__address">{result.address}</div>

						{/* Call For Information, do not show in the map info windows */}
						{result.showCallForInfo && !isInInfoWindow && (
							<div className="home-info__from">Call for Information</div>
						)}

						{/* Pricing Info */}
						{result.prices[0] > 0 && (
							<div
								className="home-info__from"
								dangerouslySetInnerHTML={{
									__html: getPriceMinLabel(result.prices[0]),
								}}
							></div>
						)}
						{!result.showCallForInfo && (
							<div className="home-info__stats">
								{/* Bedrooms */}
								{result.hasBedrooms &&
									getMinMaxLabel(result.bedrooms[0], result.bedrooms[1]) +
									" bed"}
								{/* Bathrooms */}
								{result.hasBathrooms && <span className="vert-divider">|</span>}
								{result.hasBathrooms &&
									getMinMaxLabel(result.bathrooms[0], result.bathrooms[1]) +
									" bath"}


								{/* Do not show garage and stories on info card */}
								{!isInInfoWindow && (
									<>
										{/* Garages */}
										{result.hasGarage && (
											<span className="vert-divider">|</span>
										)}
										{result.hasGarage &&
											getMinMaxLabel(result.garages[0], result.garages[1]) +
											" garage"}

									</>
								)}
								{/* Stories */}
								{result.hasStories && (
									<span className="vert-divider">|</span>
								)}
								{result.hasStories &&
									getMinMaxLabel(result.stories[0], result.stories[1]) +
									" story"}
							</div>
						)}
						{result.hasSize && !result.showCallForInfo && !isInInfoWindow && (
							<div className="home-info__square-foot">
								{getSizeLabel(result.size[0])}
							</div>
						)}
						{hasAvailableHomes && (
							<div className="home-info__available-homes">
								<object>
									<a
										href={result.url + "#relatedmovein"}
										className="CoveoResultLink"
									>
										{result.availableHomes} {result.availableHomes === 1 ? "home" : "homes"} for sale
									</a>
								</object>
							</div>
						)}
					</div>
				</div>
			</a>
		</div>
	);
};

export default ResultCard;
